import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowRight, CheckCircle, Home } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosHttpClient, HttpError } from "../../../services/AxiosHttpService";

interface VerificationResult {
  success: boolean;
  message: string;
  error?: string;
}

export default function EmailVerification() {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const verifyEmail = () => {
    if (!uuid) {
      setVerificationStatus({
        success: false,
        message: "Token de vérification manquant",
        error: "Le lien semble être invalide",
      });
      setIsLoading(false);
      return;
    }

    // Call the backend API to confirm the account
    AxiosHttpClient.getForNonAuth<string>(
      `api/v1/docuveritas/account/confirm/${uuid}`
    )
      .then((response) => {
        setVerificationStatus({
          success: true,
          message:
            response || "Votre adresse email a été vérifiée avec succès !",
        });
      })
      .catch((error: any) => {
        console.error("Error verifying email:", error);

        // Handle different types of errors
        const isHttpError =
          error && typeof error === "object" && "type" in error;

        if (isHttpError) {
          const httpError = error as HttpError;
          setVerificationStatus({
            success: false,
            message: "Erreur lors de la vérification",
            error: httpError.cause || "Lien de confirmation invalide ou expiré",
          });
        } else {
          setVerificationStatus({
            success: false,
            message: "Erreur lors de la vérification",
            error: "Une erreur s'est produite. Veuillez réessayer.",
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    verifyEmail();
  }, [uuid]);

  const handleLogin = () => {
    navigate("/signin");
  };

  const handleHome = () => {
    navigate("/");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Vérification en cours...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div
            className={`mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center ${
              verificationStatus?.success ? "bg-green-100" : "bg-red-100"
            }`}
          >
            {verificationStatus?.success ? (
              <CheckCircle className="w-8 h-8 text-green-600" />
            ) : (
              <div className="w-8 h-8 text-red-600">✕</div>
            )}
          </div>
          <CardTitle
            className={`text-2xl font-bold ${
              verificationStatus?.success ? "text-green-900" : "text-red-900"
            }`}
          >
            {verificationStatus?.success
              ? "Email vérifié !"
              : "Vérification échouée"}
          </CardTitle>
          <CardDescription className="text-gray-600">
            {verificationStatus?.message}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {verificationStatus?.success ? (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-sm text-green-800">
                  <p className="font-medium mb-2">Félicitations ! 🎉</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Votre compte est maintenant activé</li>
                    <li>• Vous pouvez vous connecter à votre espace</li>
                    <li>• Toutes les fonctionnalités sont disponibles</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleLogin}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Se connecter maintenant
                </Button>

                <Button
                  onClick={handleHome}
                  variant="outline"
                  className="w-full bg-transparent"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Retour à l'accueil
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="text-sm text-red-800">
                  <p className="font-medium mb-2">Que s'est-il passé ?</p>
                  <p className="text-xs mb-2">{verificationStatus?.error}</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Le lien a peut-être expiré</li>
                    <li>• Le lien a déjà été utilisé</li>
                    <li>• Une erreur technique s'est produite</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/signup")}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Créer un nouveau compte
                </Button>

                <Button
                  onClick={handleHome}
                  variant="outline"
                  className="w-full bg-transparent"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Retour à l'accueil
                </Button>
              </div>

              <div className="text-center">
                <p className="text-xs text-gray-500">
                  Besoin d'aide ?{" "}
                  <button className="text-blue-600 hover:underline">
                    Contactez le support
                  </button>
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
